import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useLocale } from '@hooks/useLocale';
import { quickAddProductFetcher } from '@adapters/bigcommerce/hooks/product/useProduct';
import {
  ColourOption,
  SizeOption,
  getColourOptions,
  getSelectedAvailability,
  getSizeOptionsForColour,
  resolveInitialColourId,
  resolveVariant,
} from '@adapters/bigcommerce/hooks/product/variantAvailability';
import {
  AvailabilityStatus,
  MecVariant,
  QuickAddProduct,
} from '@adapters/bigcommerce/types/product';
import { colourKeyForVariantSku } from '@utils/algolia/colourKeyForVariantSku';
import type { BundleStripProduct } from '@adapters/prismic/transforms/bundleStrip';

export interface BundleLinePick {
  colourId?: number;
  sizeId?: number | null;
}

export interface BundleSelectionLine {
  code: string;
  product: QuickAddProduct;
  colours: ColourOption[];
  sizes: SizeOption[];
  selectedColourId: number | null;
  selectedSizeId: number | null;
  selectedVariant: MecVariant | undefined;
  quantity: number;
  canAddToCart: boolean;
  isUnsupported: boolean;
}

export const deriveBundleLine = (
  { product: record, variantSku }: BundleStripProduct,
  product: QuickAddProduct,
  pick: BundleLinePick = {}
): BundleSelectionLine => {
  const authoredVariant = variantSku
    ? product.variants?.find(({ sku }) => sku === variantSku)
    : undefined;
  const colourKey = colourKeyForVariantSku(record, variantSku);
  const initialColourId =
    authoredVariant?.colourId ??
    resolveInitialColourId(product, {
      colourName: colourKey,
      colourSku: record.variants?.[colourKey]?.sku ?? variantSku ?? undefined,
    });
  const selectedColourId = pick.colourId ?? initialColourId;

  const sizes = getSizeOptionsForColour(product, selectedColourId).filter(
    (size) => size.existsForSelectedColour
  );
  const selectedSizeId =
    pick.sizeId !== undefined
      ? pick.sizeId
      : authoredVariant?.sizeId != null &&
        authoredVariant.colourId === selectedColourId
      ? authoredVariant.sizeId
      : sizes.length === 1
      ? sizes[0].id
      : null;

  const selectedVariant = resolveVariant(
    product,
    selectedColourId,
    selectedSizeId
  );
  const isUnsupported =
    !!product.isGiftCard || !!product.sizeSelectionMerchandising;
  const canAddToCart =
    !isUnsupported &&
    !!product.colourOptionId &&
    !!selectedVariant?.entityId &&
    !!selectedVariant.hasStockAvailable &&
    getSelectedAvailability(product, selectedVariant, selectedColourId) &&
    product.availabilityStatus !== AvailabilityStatus.unavailable;

  return {
    code: record.parentSku,
    product,
    colours: getColourOptions(product),
    sizes,
    selectedColourId,
    selectedSizeId,
    selectedVariant,
    quantity: Math.max(1, product.minPurchaseQuantity ?? 1),
    canAddToCart,
    isUnsupported,
  };
};

export type BundleSelectionStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseBundleSelectionResult {
  status: BundleSelectionStatus;
  lines: BundleSelectionLine[];
  selectColour: (code: string, colourId: number) => void;
  selectSize: (code: string, sizeId: number) => void;
  refresh: () => void;
}

export const useBundleSelection = (
  products: BundleStripProduct[],
  enabled: boolean
): UseBundleSelectionResult => {
  const locale = useLocale();
  const codes = useMemo(
    () => products.map(({ product }) => product.parentSku),
    [products]
  );

  const { data, error, mutate } = useSWR(
    enabled && locale ? ['bundle-quick-add', locale, ...codes] : null,
    () =>
      Promise.all(codes.map((code) => quickAddProductFetcher(locale, code))),
    { revalidateOnFocus: false, dedupingInterval: 60_000 }
  );

  const [picks, setPicks] = useState<Record<string, BundleLinePick>>({});

  const lines = useMemo(
    () =>
      data && data.every(Boolean)
        ? products.map((bundleProduct, i) =>
            deriveBundleLine(
              bundleProduct,
              data[i] as QuickAddProduct,
              picks[bundleProduct.product.parentSku]
            )
          )
        : [],
    [data, products, picks]
  );

  const selectColour = useCallback(
    (code: string, colourId: number) =>
      setPicks((prev) => {
        const index = codes.indexOf(code);
        const product = data?.[index];
        const sizeId = product
          ? deriveBundleLine(products[index], product, prev[code])
              .selectedSizeId
          : null;
        const keepsSize =
          sizeId != null &&
          (product?.variants ?? []).some(
            (variant) =>
              variant.colourId === colourId &&
              variant.sizeId === sizeId &&
              variant.hasStockAvailable
          );
        return {
          ...prev,
          [code]: { colourId, ...(keepsSize ? { sizeId } : {}) },
        };
      }),
    [data, codes, products]
  );

  const selectSize = useCallback(
    (code: string, sizeId: number) =>
      setPicks((prev) => ({ ...prev, [code]: { ...prev[code], sizeId } })),
    []
  );

  const refresh = useCallback(() => {
    void mutate();
  }, [mutate]);

  let status: BundleSelectionStatus = 'ready';
  if (!enabled) status = 'idle';
  else if (error || (data && !data.every(Boolean))) status = 'error';
  else if (!data) status = 'loading';

  return {
    status,
    lines,
    selectColour,
    selectSize,
    refresh,
  };
};

export default useBundleSelection;
